import React, {Fragment, useEffect, useState, useContext} from 'react';
import {MasterSchemaView} from '../master-schema-view';
import {get, isUndefined, omit} from 'lodash';
import {Select, Row, Col, Button, Dropdown, Popconfirm, Typography, Space} from 'antd';
import {PlusCircleOutlined, DownOutlined, DeleteOutlined} from '@ant-design/icons';
import {OpenApiContext} from '../../openapi-context';

const schema = (content, idx = 0) => content && get(Object.values(content)[idx], 'schema');

function RequestBodyViewer({body, types}) {
    const [currentType, setCurrentType] = useState(0);

    useEffect(() => {
        const contentKeys = Object.keys(body.content || {});
        if (contentKeys.length > 0) {
            setCurrentType(0);
        }
    }, [body]);

    return (
        <Fragment>
            {types.length > 1 ? (
                <Select style={{width: '100%'}} value={currentType} onChange={setCurrentType}>
                    {types.map((t, idx) => (
                        <Select.Option key={idx} value={idx}>
                            {t}
                        </Select.Option>
                    ))}
                </Select>
            ) : (
                <Typography.Text style={{marginLeft: '0.5em'}} strong>
                    {types[0] && `Content Type: ${types[0]}`}
                </Typography.Text>
            )}
            <MasterSchemaView schema={schema(body.content, currentType)} />
        </Fragment>
    );
}

function RequestBodyEditor({body, types, editMode, onChange}) {
    const [currentType, setCurrentType] = useState();
    const {state, dispatch} = useContext(OpenApiContext);

    useEffect(() => {
        if (!isUndefined(state.currentIndexRequestBody)) {
            setCurrentType(state.currentIndexRequestBody);
        } else if (Object.keys(types).length > 0) {
            setCurrentType(0);
        }
    }, [state.currentIndexRequestBody]);

    function handleChange(schemaChangeSet) {
        let action = null;
        if (schemaChangeSet.name && !schemaChangeSet.op) {
            schemaChangeSet.op = 'move';
            action = {type: 'update-body-content-schema', changeSet: omit(schemaChangeSet, 'state', 'key')};
        } else {
            action = {type: 'update-body-content-schema', changeSet: omit(schemaChangeSet, 'state', 'key')};
        }
        onChange(action);
    }

    function addSchemaType(type) {
        let mimeTypesCheck = types.includes(type.key);
        if (!mimeTypesCheck) {
            const action = {type: 'add-body-content-type', contentType: type.key, value: {schema: {type: 'object', properties: {}}}};
            dispatch({type: 'setCurrentIndexRequestBody', index: types.length});
            onChange(action);
        }
    }

    function deleteSchemaType() {
        onChange({type: 'rm-body-content-type', contentType: types[currentType]});
        if (types.length > 0) {
            dispatch({type: 'setCurrentIndexRequestBody', index: 0});
        } else {
            dispatch({type: 'setCurrentIndexRequestBody', index: null});
        }
    }

    function handleChangeCurrentType(typeIndex) {
        dispatch({type: 'setCurrentIndexRequestBody', index: typeIndex});
        setCurrentType(typeIndex);
    }

    const makeContextPath = () => {
        if (!body) return;
        return `content.${types[currentType]}.schema`;
    };

    const menu = {
        onClick: addSchemaType,
        items: [
            {key: 'application/json', label: 'application/json'},
            {key: 'application/xml', label: 'application/xml'},
            {key: 'application/www-form-urlencoded', label: 'application/www-form-urlencoded'},
            {key: 'multipart/form-data', label: 'multipart/form-data'},
        ],
    };

    return (
        <Fragment>
            <Row>
                <Col xs={18}>
                    <Select style={{width: '100%'}} placeholder="No Mime type specified for this request body" value={currentType} onChange={handleChangeCurrentType}>
                        {types.map((t, idx) => (
                            <Select.Option key={idx} value={idx}>
                                {t}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col xs={5} offset={1}>
                    <Space>
                        <Dropdown menu={menu}>
                            <Button className="addButton" type="primary" icon={<PlusCircleOutlined />}>
                                Add <DownOutlined />
                            </Button>
                        </Dropdown>
                        <Popconfirm okType="default" title="Remove this content type?" onConfirm={deleteSchemaType}>
                            <Button type="danger" className="deleteButton" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <MasterSchemaView schema={schema(body.content, currentType)} contextPath={makeContextPath()} editMode={editMode} onChange={handleChange} />
                </Col>
            </Row>
        </Fragment>
    );
}

// Note: this component used to wrap its content in <ToggleSection title="Request Body">,
// but operation-block.js renders it inside an antd <Collapse> panel that
// already provides a "Request Body" header + collapse caret. Two layers
// of show/hide chrome with the same title was visible duplication. Now
// the body content is rendered flat — the parent Collapse's panel header
// owns the title; the delete button is placed in the panel's `extra`
// slot via the `RequestBodyDeleteButton` export below.
export function RequestBody({body, editMode, onChange}) {
    const [types, setTypes] = useState([]);

    useEffect(() => {
        if (body) {
            setTypes(Object.keys(body.content || {}));
        }
    }, [body]);

    return body ? (
        editMode
            ? <RequestBodyEditor types={types} body={body} onChange={onChange} editMode={editMode} />
            : <RequestBodyViewer types={types} body={body} />
    ) : null;
}

// Rendered in the parent Collapse panel's `extra` slot so the delete
// button sits next to the panel's caret (same place ToggleSection used
// to put it). Stops propagation on click so the antd Collapse doesn't
// toggle when the user fires the Popconfirm.
export function RequestBodyDeleteButton({onChange}) {
    function handleRemoveRequestBody() {
        onChange({type: 'rm-request-body'});
    }
    return (
        <Popconfirm okType="default" title="Remove Request Body from this operation?" onConfirm={handleRemoveRequestBody}>
            <Button
                className="deleteButton"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
            />
        </Popconfirm>
    );
}
